/**
 * ADHD module unlock helpers — especially SLaM / 3 Borough (SSI-first).
 *
 * Product rule: once Clinical Interview is first completed for an SSI-first
 * workflow, Diagnostic Decision and Post-Assessment Discussion become
 * accessible on the patient overview. CI/SSI report approval remains a
 * Documents quality gate; it must not keep DD/PAD cards locked.
 *
 * Non-SLaM orgs keep the existing behaviour:
 *   - DD waits until CI (+ SSI when applicable) reports are approved
 *   - PAD waits until its stage status is no longer `locked` (usually after DD)
 *
 * One-clinician SLaM / 3 Borough (Oct 2026, wf.appointmentFinishPathModule ===
 * 'ssi'): a single Lead runs CI then SSI in one appointment, so the gates move
 * one module along:
 *   - SSI is locked until the Lead chooses "Proceed to Semi-Structured
 *     Interview" at the end of the CI (workflowDecisions.ciEndDecision);
 *   - DD and PAD unlock once the SSI is first completed (the PAD-now / defer
 *     wizard at the end of the SSI Review writes their stage statuses), again
 *     without waiting for report approval;
 *   - when the Lead chose "End assessment" (wf.assessmentEndedAfterCI) every
 *     module after the CI is locked until the assessment is resumed from the
 *     Patient Overview banner.
 *
 * Overview lock predicates are pure so they can be unit-tested without mounting
 * PatientOverview, and so Path 1/2 CI-finish writers stay aligned with the UI.
 */

import { shouldUnlockPadOnDdComplete } from './padUnlockUtils';
import { CI_END_DECISIONS } from '../constants/workflowConfig';

/** Tooltip for every post-CI card while the assessment is ended. */
export const ASSESSMENT_ENDED_LOCK_TOOLTIP =
  'The assessment was ended after the Clinical Interview. Resume the assessment to continue.';

/** Tooltip for the SSI card before the Lead has chosen to proceed. */
export const SSI_AWAITING_CI_DECISION_TOOLTIP =
  'Finish the Clinical Interview and choose to proceed to the Semi-Structured Interview to unlock this module.';

/** Tooltip for DD / PAD in the one-clinician workflow before SSI completes. */
export const AWAITING_SSI_COMPLETION_TOOLTIP =
  'Complete the Semi-Structured Interview to unlock this module.';

/**
 * True when the Semi-Structured Interview has been completed at least once
 * (same resilience rule as isClinicalInterviewCompleted).
 */
export function isSemiStructuredInterviewCompleted(patientDataModel) {
  const ssi = patientDataModel?.dataModel?.['3_semiStructuredInterview'];
  if (!ssi) return false;
  return ssi.status === 'completed' || Boolean(ssi.lastCompletedAt);
}

/**
 * One-clinician SLaM / 3 Borough workflow: the PAD-now / defer wizard runs at
 * the end of the SSI Review (stamp-only; legacy SLaM patients stay SSI-first).
 */
export function isSsiFinishPathAdhdUnlockWorkflow(wf) {
  return wf?.appointmentFinishPathModule === 'ssi';
}

/**
 * One-clinician workflow: DD + PAD open once the SSI is first completed,
 * mirroring shouldUnlockDdPadAfterCiComplete one module along.
 */
export function shouldUnlockDdPadAfterSsiComplete(wf, patientDataModel) {
  return (
    isSsiFinishPathAdhdUnlockWorkflow(wf) &&
    isSemiStructuredInterviewCompleted(patientDataModel)
  );
}

/**
 * True when Clinical Interview has been completed at least once.
 * Prefer status === 'completed'; also accept lastCompletedAt for resilience
 * if status was somehow left inconsistent after a partial write.
 */
export function isClinicalInterviewCompleted(patientDataModel) {
  const ci = patientDataModel?.dataModel?.['2_clinicalInterview'];
  if (!ci) return false;
  return ci.status === 'completed' || Boolean(ci.lastCompletedAt);
}

/**
 * SLaM / 3 Borough ADHD finish-path workflow (stamped or legacy SSI-first).
 * Prefer hasCIReview (finish wizard orgs) but also honour isSSIFirst so legacy
 * patients without stamped ciFlow still unlock after CI.
 */
export function isSsiFirstAdhdUnlockWorkflow(wf) {
  return Boolean(wf?.hasCIReview || wf?.isSSIFirst);
}

/**
 * After CI completes, SSI-first orgs unlock DD + PAD without waiting for
 * report approval. Used by overview locks and dashboard progress mirrors.
 */
export function shouldUnlockDdPadAfterCiComplete(wf, patientDataModel) {
  return (
    isSsiFirstAdhdUnlockWorkflow(wf) &&
    isClinicalInterviewCompleted(patientDataModel)
  );
}

/**
 * True when DD stage is still locked (or unset) and may be set to notStarted.
 * Never overwrite inProgress / completed / notStarted.
 */
export function shouldUnlockDdStageIfLocked(patientDataModel) {
  const status =
    patientDataModel?.dataModel?.['4_diagnosticDecision']?.status;
  return !status || status === 'locked';
}

/**
 * Stage status writes to apply when SLaM finishes CI (Path 1 or Path 2).
 * Returns only keys that should change — never regresses open stages.
 *
 * @returns {{ unlockPad: boolean, unlockDd: boolean }}
 */
export function resolveDdPadUnlockOnCiComplete(patientDataModel) {
  return {
    unlockPad: shouldUnlockPadOnDdComplete(patientDataModel),
    unlockDd: shouldUnlockDdStageIfLocked(patientDataModel),
  };
}

/**
 * ADHD overview: should the Semi-Structured Interview card be locked?
 *
 * Only the one-clinician SLaM workflow ever locks the SSI: the Lead must first
 * finish the CI and choose "Proceed". Once the SSI has been started or
 * completed it stays open (the decision key may be absent on historical
 * patients or after a resume), except while the assessment is ended.
 *
 * Every other workflow returns false here; PatientOverview still applies its
 * ADHD-applicability "disabled" rule separately.
 *
 * @param {Object} args
 * @param {Object|null} args.patientDataModel
 * @param {Object} args.wf
 */
export function shouldLockAdhdSemiStructuredInterview({ patientDataModel, wf }) {
  if (wf?.assessmentEndedAfterCI) return true;
  if (!isSsiFinishPathAdhdUnlockWorkflow(wf)) return false;

  const ssi = patientDataModel?.dataModel?.['3_semiStructuredInterview'];
  const ssiStarted =
    ssi?.status === 'inProgress' || isSemiStructuredInterviewCompleted(patientDataModel);
  if (ssiStarted) return false;

  // The CI must be completed before the SSI can be unlocked. For SLaM the SSI
  // card is alwaysVisible (no ADHD-disabled gate), so this is the only thing
  // preventing a clinician from opening the SSI on a fresh patient.
  const ciCompleted =
    patientDataModel?.dataModel?.['2_clinicalInterview']?.status === 'completed';
  if (!ciCompleted) return true;

  // CI is completed. Absent/null ciEndDecision means the decision was cleared
  // (resume deletes the key) or was never written (legacy patient). In both
  // cases the SSI is available. Only lock when a decision exists AND is
  // something other than "proceed" — in practice only an unknown future value,
  // since 'endAssessment' is caught by the assessmentEndedAfterCI guard above.
  return wf?.ciEndDecision != null
    && wf.ciEndDecision !== CI_END_DECISIONS.PROCEED_TO_SSI;
}

/**
 * Tooltip for a locked SSI card. Empty when not locked.
 */
export function getAdhdSemiStructuredInterviewLockedTooltip({ patientDataModel, wf }) {
  if (!shouldLockAdhdSemiStructuredInterview({ patientDataModel, wf })) return '';
  if (wf?.assessmentEndedAfterCI) return ASSESSMENT_ENDED_LOCK_TOOLTIP;
  return SSI_AWAITING_CI_DECISION_TOOLTIP;
}

/**
 * ADHD overview: should the Diagnostic Decision card be locked?
 *
 * @param {Object} args
 * @param {Object|null} args.patientDataModel
 * @param {Object} args.wf - resolveWorkflowConfig / useWorkflowConfig result
 * @param {boolean} args.isSsiDisabled - true when SSI module is N/A (non-ADHD)
 */
export function shouldLockAdhdDiagnosticDecision({
  patientDataModel,
  wf,
  isSsiDisabled = false,
}) {
  // One-clinician SLaM: an ended assessment locks everything after the CI,
  // including a DD that was completed before a resume-then-end cycle. The
  // card must not offer "Review" into a module the assessment no longer runs.
  if (wf?.assessmentEndedAfterCI) return true;

  const ddPreviouslyCompleted = Boolean(
    patientDataModel?.dataModel?.['4_diagnosticDecision']?.lastCompletedAt
  );
  if (ddPreviouslyCompleted) return false;

  // One-clinician SLaM: DD opens once the SSI is first completed; before that
  // it is locked regardless of report approval state.
  if (isSsiFinishPathAdhdUnlockWorkflow(wf)) {
    return !shouldUnlockDdPadAfterSsiComplete(wf, patientDataModel);
  }

  // SLaM / 3 Borough: CI complete opens DD even if AI outputs are unapproved.
  if (shouldUnlockDdPadAfterCiComplete(wf, patientDataModel)) {
    return false;
  }

  const manageReports = patientDataModel?.dataModel?.['6_manageReports'];
  const ciNeedsApproval = manageReports?.ciReportApproved === false;
  const ssiNeedsApproval =
    !isSsiDisabled && manageReports?.ssiReportApproved === false;
  return ciNeedsApproval || ssiNeedsApproval;
}

/**
 * ADHD overview: should the Post-Assessment Discussion card be locked?
 *
 * @param {Object} args
 * @param {Object|null} args.patientDataModel
 * @param {Object} args.wf
 * @param {boolean} args.isPadDisabled - true when PAD N/A (non-ADHD path)
 */
export function shouldLockAdhdPostAssessmentDiscussion({
  patientDataModel,
  wf,
  isPadDisabled = false,
}) {
  if (isPadDisabled) return true;

  // One-clinician SLaM: ended assessment locks PAD; otherwise PAD opens once
  // the SSI is first completed (the SSI finish wizard writes its status).
  if (wf?.assessmentEndedAfterCI) return true;
  if (isSsiFinishPathAdhdUnlockWorkflow(wf)) {
    return !shouldUnlockDdPadAfterSsiComplete(wf, patientDataModel);
  }

  // SLaM / 3 Borough: CI complete opens PAD even if stage status is still
  // stamped `locked` (legacy patients / Path 2 before status write lands).
  if (shouldUnlockDdPadAfterCiComplete(wf, patientDataModel)) {
    return false;
  }

  return (
    patientDataModel?.dataModel?.['5_postAssessmentDiscussion']?.status ===
    'locked'
  );
}

/**
 * Tooltip for a locked PAD card. Only the one-clinician workflow explains PAD
 * locks (other workflows have no tooltip on PAD today). Empty when not locked.
 */
export function getAdhdPostAssessmentDiscussionLockedTooltip({
  patientDataModel,
  wf,
  isPadDisabled = false,
}) {
  if (!shouldLockAdhdPostAssessmentDiscussion({ patientDataModel, wf, isPadDisabled })) return '';
  if (wf?.assessmentEndedAfterCI) return ASSESSMENT_ENDED_LOCK_TOOLTIP;
  if (isSsiFinishPathAdhdUnlockWorkflow(wf)) return AWAITING_SSI_COMPLETION_TOOLTIP;
  return '';
}

/**
 * Tooltip for a locked DD card. Empty when not locked.
 */
export function getAdhdDiagnosticDecisionLockedTooltip({
  patientDataModel,
  wf,
  isSsiDisabled = false,
}) {
  if (
    !shouldLockAdhdDiagnosticDecision({
      patientDataModel,
      wf,
      isSsiDisabled,
    })
  ) {
    return '';
  }

  if (wf?.assessmentEndedAfterCI) return ASSESSMENT_ENDED_LOCK_TOOLTIP;
  if (isSsiFinishPathAdhdUnlockWorkflow(wf)) return AWAITING_SSI_COMPLETION_TOOLTIP;

  const manageReports = patientDataModel?.dataModel?.['6_manageReports'];
  const ciNeedsReview = manageReports?.ciReportApproved === false;
  const ssiNeedsReview =
    !isSsiDisabled && manageReports?.ssiReportApproved === false;

  if (ciNeedsReview && ssiNeedsReview) {
    return 'The Clinical Interview and Semi-Structured Interview AI outputs need to be reviewed and approved for this module to become unlocked.';
  }
  if (ciNeedsReview) {
    return 'The Clinical Interview AI outputs need to be reviewed and approved for this module to become unlocked.';
  }
  if (ssiNeedsReview) {
    return 'The Semi-Structured Interview AI outputs need to be reviewed and approved for this module to become unlocked.';
  }
  return '';
}
